import React, { createContext, useContext, useState, ReactNode, useMemo, useEffect } from 'react';
import { useSupabaseFoundationSearch } from '../hooks/useSupabaseFoundationSearch';
import { isMockModeEnabled } from '../services/apiServiceFactory';
import { Foundation } from '../types/Foundation';

interface SearchFilters {
  region: string;
  onlyActive: boolean;
  purpose: string;
}

interface SupabaseSearchContextType {
  query: string;
  setQuery: (query: string) => void;
  searchResults: Foundation[];
  filteredResults: Foundation[];
  totalFoundations: number;
  totalResults: number;
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  nextPage: () => void;
  previousPage: () => void;
  goToPage: (page: number) => void;
  retryFetch: () => void;
  refreshData: () => void;
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  isMockMode: boolean;
}

const SupabaseSearchContext = createContext<SupabaseSearchContextType | undefined>(undefined);

export function SupabaseSearchProvider({ children }: { children: ReactNode }) {
  const [query, setQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    region: '',
    onlyActive: false,
    purpose: ''
  });
  const [isMockMode, setIsMockMode] = useState(false);
  
  // Check if mock mode is enabled on component mount
  useEffect(() => {
    // In production, always force mock mode to be false
    if (process.env.NODE_ENV === 'production') {
      setIsMockMode(false);
    } else {
      setIsMockMode(isMockModeEnabled());
    }
  }, []);
  
  const { 
    searchResults,
    totalFoundations,
    loading, 
    error, 
    searchFoundations,
    currentPage,
    totalPages,
    nextPage,
    previousPage,
    goToPage,
    retryFetch,
    refreshData
  } = useSupabaseFoundationSearch();

  // Filter the search results based on additional criteria
  const filteredSearchResults = useMemo(() => {
    return searchResults.filter((foundation: Foundation) => {
      // Filter by region if specified
      if (searchFilters.region && foundation.region !== searchFilters.region) {
        return false;
      }

      // Filter by active status
      if (searchFilters.onlyActive && !foundation.firmaTeckning) {
        return false;
      }

      // Filter by purpose if specified
      if (searchFilters.purpose && !foundation.purpose?.toLowerCase().includes(searchFilters.purpose.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [searchResults, searchFilters]);
  
  // The backend API already handles pagination, so we don't need to slice the results
  const filteredResults = filteredSearchResults;

  const handleSearch = (newQuery: string) => {
    setQuery(newQuery);
    
    // Debugging log to help diagnose issues
    console.log("Searching with:", { 
      query: newQuery || "(empty)", 
      region: searchFilters.region || "(no region)" 
    });
    
    // Force a search with the region filter, even with empty query
    // This will allow searching by region only
    searchFoundations(newQuery, 1, searchFilters.region);
  };

  // Effect to re-run search when filters change
  useEffect(() => {
    // Only trigger a search if we've already performed at least one search
    if (query) {
      console.log("Filter changed, re-running search with:", searchFilters.region);
      searchFoundations(query, 1, searchFilters.region);
    }
  }, [searchFilters.region, searchFoundations, query]);

  return (
    <SupabaseSearchContext.Provider value={{
      query,
      setQuery: handleSearch,
      searchResults,
      filteredResults,
      totalFoundations,
      totalResults: filteredSearchResults.length,
      loading,
      error,
      currentPage,
      totalPages,
      nextPage,
      previousPage,
      goToPage,
      retryFetch,
      refreshData,
      searchFilters,
      setSearchFilters,
      isMockMode
    }}>
      {children}
    </SupabaseSearchContext.Provider>
  );
}

export function useSupabaseSearch() {
  const context = useContext(SupabaseSearchContext);
  if (context === undefined) {
    throw new Error('useSupabaseSearch must be used within a SupabaseSearchProvider');
  }
  return context;
}
